import type {Nominal} from "@/service/typing.ts";

export enum EWeekday {
  Mon = 1,
  Tue = 2,
  Wed = 3,
  Thu = 4,
  Fri = 5,
  Sat = 6,
  Sun = 0,
}

export namespace EWeekday {
  export function fromDate(date: Date): EWeekday {
    return date.getDay() as EWeekday
  }
}

export type IOpeningHours = Record<EWeekday, false | IOpeningHours.Entry>

export namespace IOpeningHours {
  export interface Entry {
    openingHours: TimeRange
    lunchBreak: false | TimeRange
  }

  export type Time = Nominal<number, 'Time'>

  export type TimeRange = readonly [Time, Time]

  const timeRegex = /^(?<hh>\d{1,2}):(?<mm>\d{2})$/

  export function parseTime(value: string): false | Time {
    if (!value) {
      return false
    }
    const match = timeRegex.exec(value)
    if (!match) {
      console.warn("Invalid time: ", value)
      return false
    }
    const {hh, mm} = match.groups ?? {}
    const hour = Number.parseInt(hh, 10)
    const minute = Number.parseInt(mm, 10)
    const time = (hour * 60) + minute
    return Number.isNaN(time) ? false : (time as Time)
  }

  export function timeOfDate(date: Date): Time {
    const hour = date.getHours()
    const minute = date.getMinutes()
    const time = (hour * 60) + minute
    return time as Time
  }

  export function formatTime(value: Time): string {
    const hour = Math.floor(value / 60)
    const minute = Math.floor(value % 60)
    const hh = hour.toString(10).padStart(2, '0')
    const mm = minute.toString(10).padStart(2, '0')
    return `${hh}:${mm}`
  }

  export function inRange(time: Time, range: TimeRange): boolean {
    const [start, end] = range
    const actualEnd = (end <= start) ? (end + 24 * 60) as Time : end
    return (start <= time) && (time <= actualEnd)
  }

  function parseOpeningHoursEntry(open: string, lunchStart: string, lunchEnd: string, close: string): false | Entry {
    const openTime = parseTime(open)
    const closeTime = parseTime(close)
    if (openTime === false || closeTime === false) {
      return false // No open/close time -> No entry for day opening hours
    }

    const openingHours = [openTime, closeTime] as const

    const lunchStartTime = parseTime(lunchStart)
    const lunchEndTime = parseTime(lunchEnd)
    const lunchBreak = (lunchStartTime !== false && lunchEndTime !== false)
      ? [lunchStartTime, lunchEndTime] as const
      : false

    return {openingHours, lunchBreak}
  }

  export function parseOpeningHoursFromArray(data: readonly string[]): IOpeningHours {
    const [
      mon0 = "", mon1 = "", mon2 = "", mon3 = "",
      tue0 = "", tue1 = "", tue2 = "", tue3 = "",
      wed0 = "", wed1 = "", wed2 = "", wed3 = "",
      thu0 = "", thu1 = "", thu2 = "", thu3 = "",
      fri0 = "", fri1 = "", fri2 = "", fri3 = "",
      sat0 = "", sat1 = "", sat2 = "", sat3 = "",
      sun0 = "", sun1 = "", sun2 = "", sun3 = "",
    ] = data

    return {
      [EWeekday.Mon]: parseOpeningHoursEntry(mon0, mon1, mon2, mon3),
      [EWeekday.Tue]: parseOpeningHoursEntry(tue0, tue1, tue2, tue3),
      [EWeekday.Wed]: parseOpeningHoursEntry(wed0, wed1, wed2, wed3),
      [EWeekday.Thu]: parseOpeningHoursEntry(thu0, thu1, thu2, thu3),
      [EWeekday.Fri]: parseOpeningHoursEntry(fri0, fri1, fri2, fri3),
      [EWeekday.Sat]: parseOpeningHoursEntry(sat0, sat1, sat2, sat3),
      [EWeekday.Sun]: parseOpeningHoursEntry(sun0, sun1, sun2, sun3),
    }
  }
}

export type ICurrentlyOpen =
  | { open: false }
  | { open: true, remaining: IOpeningHours.Time }

export namespace ICurrentlyOpen {
  export function evaluate(day: EWeekday, time: IOpeningHours.Time, hours: IOpeningHours): ICurrentlyOpen {
    // Look up opening hours for day, bail if store is closed for entire day
    const entry = hours[day]
    if (!entry) {
      return {open: false}
    }

    // Check if time is within opening hours, bail if outside opening hours
    if (!IOpeningHours.inRange(time, entry.openingHours)) {
      return {open: false}
    }

    // Handle stores with lunch break
    if (entry.lunchBreak) {
      // Check if time is within lunch break, bail if within lunch break
      if (IOpeningHours.inRange(time, entry.lunchBreak)) {
        return {open: false}
      }

      // Calculate remaining time until lunch break, return remaining time if lunch break is ahead
      const [lunchBreakStart] = entry.lunchBreak
      const timeUntilLunchBreak = (lunchBreakStart - time) as IOpeningHours.Time
      if (timeUntilLunchBreak > 0) {
        return {open: true, remaining: timeUntilLunchBreak}
      }
    }

    const [startTime, endTime] = entry.openingHours
    const actualEndTime = (startTime >= endTime) ? (endTime + 24 * 60) as IOpeningHours.Time : endTime
    const timeUntilClose = (actualEndTime - time) as IOpeningHours.Time
    return {open: true, remaining: timeUntilClose}
  }

  export function formatRemaining(remaining: IOpeningHours.Time): string {
    const hours = Math.floor(remaining / 60)
    const minutes = Math.floor(remaining % 60)
    if (hours > 1) {
      return `${hours} Stunden`
    } else if (hours > 0) {
      if (minutes > 1) return `1 Stunde ${minutes} Minuten`
      else if (minutes > 0) return `1 Stunde 1 Minute`
      else return `1 Stunde`
    } else {
      if (minutes > 1) return `${minutes} Minuten`
      else return `1 Minute`
    }
  }
}
